// piano-frontend/src/components/ScheduleConflictPreview.js
import React, { useState, useEffect } from "react";
import {
  Box, Typography, Paper, CircularProgress, Stack,
  List, ListItem, ListItemIcon, ListItemText, Chip, Button,
  Collapse
} from "@mui/material";
import {
  Error as ErrorIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  CheckCircle,
  ExpandMore,
  ExpandLess
} from "@mui/icons-material";
import api from "../api/axios";
import { useDarkMode } from "../App";

const WD_LABEL = ["일", "월", "화", "수", "목", "금", "토"];
// "YYYY-MM-DD" → "M/D(요일)"
const fmtDate = (iso) => {
  const [y, m, d] = String(iso).split("-").map(Number);
  if (!y || !m || !d) return String(iso);
  return `${m}/${d}(${WD_LABEL[new Date(y, m - 1, d).getDay()]})`;
};
// 경고의 날짜별 점유 상세 표기: 레슨실 점유는 "방 · 이름", 관리자/선생님 예약은 블록, 그 외 회원 레슨
const occupantLabel = (o) =>
  o.room ? `${o.room} · ${o.name}`.trim()
    : (o.role === "ADMIN" || o.role === "TEACHER") ? `${o.name} (블록)`
    : `${o.name} 레슨`;

function ScheduleConflictPreview({ memberId, newSchedules, onSimulationComplete }) {
  const { darkMode } = useDarkMode();
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [expanded, setExpanded] = useState({ conflicts: true, warnings: true });

  const runSimulation = async () => {
    if (!newSchedules || newSchedules.length === 0) {
      setResult(null);
      return;
    }

    setLoading(true);
    try {
      const res = await api.post("/members/schedules/simulate", {
        member_id: memberId,
        new_schedules: newSchedules.map(s => ({
          day: s.day,
          start: s.start,
          end: s.end,
          teacher_id: s.teacher_id
        }))
      });
      setResult(res.data);
      onSimulationComplete && onSimulationComplete(res.data);
    } catch (err) {
      console.error("시뮬레이션 실패:", err);
      setResult({
        canProceed: false,
        conflicts: [{ type: 'ERROR', message: '시뮬레이션 중 오류가 발생했습니다.' }],
        warnings: []
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      runSimulation();
    }, 500); // 디바운스

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memberId, JSON.stringify(newSchedules)]);

  if (!newSchedules || newSchedules.length === 0) {
    return null;
  }

  if (loading) {
    return (
      <Box display="flex" alignItems="center" gap={1} py={1}>
        <CircularProgress size={20} />
        <Typography variant="body2" color="text.secondary">
          충돌 검사 중...
        </Typography>
      </Box>
    );
  }

  if (!result) return null;

  const { canProceed, conflicts, warnings, summary } = result;

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        mt: 2,
        bgcolor: canProceed
          ? (darkMode ? "rgba(76, 175, 80, 0.12)" : "#f1f8e9")
          : (darkMode ? "rgba(255, 152, 0, 0.12)" : "#fff3e0"),
        borderColor: canProceed
          ? (darkMode ? "rgba(76, 175, 80, 0.5)" : "#8bc34a")
          : (darkMode ? "rgba(255, 152, 0, 0.5)" : "#ff9800")
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1} mb={1}>
        {canProceed ? (
          <>
            <CheckCircle color="success" />
            <Typography fontWeight={600} color="success.main">
              충돌 없음 - 저장 가능
            </Typography>
          </>
        ) : (
          <>
            <ErrorIcon color="error" />
            <Typography fontWeight={600} color="error.main">
              충돌 발견 - 수정 필요
            </Typography>
          </>
        )}
        
        <Box flex={1} />
        
        <Button size="small" onClick={runSimulation} disabled={loading}>
          다시 검사
        </Button>
      </Stack>

      {/* 충돌 목록 */}
      {conflicts && conflicts.length > 0 && (
        <Box mb={2}>
          <Stack 
            direction="row" 
            alignItems="center" 
            onClick={() => setExpanded(prev => ({ ...prev, conflicts: !prev.conflicts }))}
            sx={{ cursor: "pointer" }}
          >
            <ErrorIcon color="error" fontSize="small" sx={{ mr: 1 }} />
            <Typography variant="subtitle2" fontWeight={600} color="error.main">
              충돌 ({conflicts.length}건)
            </Typography>
            {expanded.conflicts ? <ExpandLess /> : <ExpandMore />}
          </Stack>
          
          <Collapse in={expanded.conflicts}>
            <List dense>
              {conflicts.map((conflict, idx) => (
                <ListItem key={idx} sx={{ py: 0.5 }}>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <ErrorIcon color="error" fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary={conflict.message}
                    secondary={
                      (conflict.existingMembers || conflict.workingHours) && (
                        <Box mt={0.5}>
                          {conflict.existingMembers && (
                            <Stack direction="row" spacing={0.5} mb={0.5}>
                              {conflict.existingMembers.map(m => (
                                <Chip 
                                  key={m.id} 
                                  size="small" 
                                  label={m.name}
                                  sx={{ fontSize: "0.7rem", height: 20 }}
                                />
                              ))}
                            </Stack>
                          )}
                          {conflict.workingHours && (
                            <Chip 
                              size="small" 
                              label={`근무시간: ${conflict.workingHours}`}
                              color="warning"
                              variant="outlined"
                              sx={{ fontSize: "0.7rem", height: 20 }}
                            />
                          )}
                        </Box>
                      )
                    }
                  />
                </ListItem>
              ))}
            </List>
          </Collapse>
        </Box>
      )}

      {/* 경고 목록 */}
      {warnings && warnings.length > 0 && (
        <Box>
          <Stack 
            direction="row" 
            alignItems="center"
            onClick={() => setExpanded(prev => ({ ...prev, warnings: !prev.warnings }))}
            sx={{ cursor: "pointer" }}
          >
            <WarningIcon color="warning" fontSize="small" sx={{ mr: 1 }} />
            <Typography variant="subtitle2" fontWeight={600} color="warning.main">
              경고 ({warnings.length}건)
            </Typography>
            {expanded.warnings ? <ExpandLess /> : <ExpandMore />}
          </Stack>
          
          <Collapse in={expanded.warnings}>
            <List dense>
              {warnings.map((warning, idx) => (
                <ListItem key={idx} sx={{ py: 0.5 }}>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    {warning.severity === 'info' ? (
                      <InfoIcon color="info" fontSize="small" />
                    ) : (
                      <WarningIcon color="warning" fontSize="small" />
                    )}
                  </ListItemIcon>
                  <ListItemText
                    primary={warning.message}
                    secondaryTypographyProps={{ component: "div" }}
                    secondary={
                      (warning.existingMembers || (warning.details || []).length > 0) ? (
                        <>
                          {warning.existingMembers && (
                            <Stack direction="row" spacing={0.5} mt={0.5}>
                              {warning.existingMembers.map(m => (
                                <Chip
                                  key={m.id}
                                  size="small"
                                  label={m.name}
                                  variant="outlined"
                                  sx={{ fontSize: "0.7rem", height: 20 }}
                                />
                              ))}
                            </Stack>
                          )}
                          {/* 날짜별 점유 상세 — 어떤 예약 때문에 건너뛰고 보강 티켓이 발행되는지 */}
                          {(warning.details || []).length > 0 && (
                            <Stack direction="row" spacing={0.5} mt={0.5} useFlexGap flexWrap="wrap">
                              {warning.details.map((d) => (
                                <Chip
                                  key={d.date}
                                  size="small"
                                  variant="outlined"
                                  color="warning"
                                  label={
                                    <>
                                      <Box component="span" sx={{ fontWeight: 700 }}>{fmtDate(d.date)}</Box>
                                      {` ${d.occupants.map(occupantLabel).join(", ")}`}
                                    </>
                                  }
                                  sx={{ fontSize: "0.7rem", height: 20 }}
                                />
                              ))}
                              {(warning.conflictingTotal || 0) > warning.details.length && (
                                <Chip
                                  size="small"
                                  label={`외 ${warning.conflictingTotal - warning.details.length}일`}
                                  sx={{ fontSize: "0.7rem", height: 20 }}
                                />
                              )}
                            </Stack>
                          )}
                        </>
                      ) : null
                    }
                  />
                </ListItem>
              ))}
            </List>
          </Collapse>
        </Box>
      )}

      {/* 요약 */}
      {summary && (
        <Box mt={2} pt={1} sx={{ borderTop: 1, borderColor: "divider" }}>
          <Typography variant="caption" color="text.secondary">
            새 스케줄: {summary.newScheduleCount}개 |
            충돌: {summary.totalConflicts}개 |
            경고: {summary.totalWarnings}개
          </Typography>
        </Box>
      )}
    </Paper>
  );
}

export default ScheduleConflictPreview;